// Shared hooks for portal-based dropdown components.
// Eliminates duplicated keyboard, positioning, focus, and outside-click logic.

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  RefObject,
} from "react";

// ── Shared types ────────────────────────────────────────────────────
export interface DropdownPos {
  /** Absolute top in viewport. Mutually exclusive with `bottom`. */
  top?: number;
  /** Distance from viewport bottom — used when flipping above the trigger. */
  bottom?: number;
  left: number;
  width: number;
  /** Max height the dropdown should render at, clamped to viewport space. */
  maxHeight: number;
}

// ── Placement helper ────────────────────────────────────────────────
const MIN_DROPDOWN_HEIGHT = 120;

/**
 * Computes a viewport-clamped placement for a portaled dropdown.
 *
 * - Picks the side (above/below) that fits `desiredHeight` first; otherwise
 *   the side with more room.
 * - Returns `maxHeight` clamped to available space so the dropdown never
 *   overflows the viewport (which would clip the search input or items).
 * - Clamps `left` so the dropdown stays inside the viewport horizontally.
 */
export function computeDropdownPlacement(
  rect: { top: number; bottom: number; left: number; width: number },
  desiredHeight: number,
  desiredWidth: number,
  viewport: { width: number; height: number },
  padding = 8,
): DropdownPos {
  const spaceBelow = viewport.height - rect.bottom - padding;
  const spaceAbove = rect.top - padding;

  const fitsBelow = spaceBelow >= desiredHeight;
  const fitsAbove = spaceAbove >= desiredHeight;

  let placeAbove: boolean;
  if (fitsBelow) placeAbove = false;
  else if (fitsAbove) placeAbove = true;
  else placeAbove = spaceAbove > spaceBelow;

  const available = Math.max(0, placeAbove ? spaceAbove : spaceBelow);
  // Clamp to available space first, then apply MIN floor — but never exceed
  // available, otherwise the panel would overflow the viewport edge.
  const preferred = Math.min(desiredHeight, available);
  const maxHeight = Math.min(
    available,
    Math.max(MIN_DROPDOWN_HEIGHT, preferred),
  );

  let left = rect.left;
  if (left + desiredWidth > viewport.width - padding) {
    left = Math.max(padding, viewport.width - desiredWidth - padding);
  }
  if (left < padding) left = padding;

  if (placeAbove) {
    return {
      bottom: viewport.height - rect.top + 4,
      left,
      width: desiredWidth,
      maxHeight,
    };
  }
  return {
    top: rect.bottom + 4,
    left,
    width: desiredWidth,
    maxHeight,
  };
}

/**
 * Returns the inline style object for a portaled dropdown panel given a
 * computed `DropdownPos`. Centralises the `top`/`bottom`/`left`/`width`/
 * `maxHeight` block that was duplicated across every consumer.
 */
export function dropdownPanelStyle(
  pos: DropdownPos,
): Pick<
  React.CSSProperties,
  "top" | "bottom" | "left" | "width" | "maxHeight"
> {
  return {
    top: pos.top === undefined ? undefined : `${pos.top}px`,
    bottom: pos.bottom === undefined ? undefined : `${pos.bottom}px`,
    left: `${pos.left}px`,
    width: `${pos.width}px`,
    maxHeight: `${pos.maxHeight}px`,
  };
}

// ── Keyboard navigation ────────────────────────────────────────────
interface UseDropdownKeyboardOptions {
  /** Total number of selectable items in the list */
  itemCount: number;
  /** Currently highlighted index */
  highlightedIndex: number;
  setHighlightedIndex: React.Dispatch<React.SetStateAction<number>>;
  /** Called when Enter is pressed on the highlighted item */
  onSelect: (index: number) => void;
  /** Called when Escape is pressed */
  onClose: () => void;
  /** Ref to focus when Escape is pressed */
  triggerRef?: RefObject<HTMLElement | null>;
  /**
   * Lowest index the keyboard may reach — and the floor Enter honors.
   * Defaults to 0. A list whose leading row is an action rather than a value
   * (the "Custom" add-row) raises this to its first real row, so no arrow key
   * — and no stale highlight left behind by a mouse hover — can make Enter
   * fire that action. The action stays reachable by click.
   */
  minIndex?: number;
}

/**
 * Returns a stable keyDown handler for ArrowUp / ArrowDown / Enter / Escape.
 * Identical logic was duplicated in PartnerButton, LocationButton,
 * EmployeeSelector, and PartnerSelectWithTags.
 */
export function useDropdownKeyboard({
  itemCount,
  highlightedIndex,
  setHighlightedIndex,
  onSelect,
  onClose,
  triggerRef,
  minIndex = 0,
}: UseDropdownKeyboardOptions) {
  return useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        if (itemCount > 0) {
          setHighlightedIndex((i) =>
            Math.max(minIndex, Math.min(i + 1, itemCount - 1)),
          );
        }
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setHighlightedIndex((i) => Math.max(i - 1, minIndex));
      } else if (e.key === "Enter") {
        e.preventDefault();
        if (highlightedIndex >= minIndex && highlightedIndex < itemCount) {
          onSelect(highlightedIndex);
        }
      } else if (e.key === "Escape") {
        e.preventDefault();
        onClose();
        triggerRef?.current?.focus();
      }
    },
    [
      itemCount,
      highlightedIndex,
      setHighlightedIndex,
      onSelect,
      onClose,
      triggerRef,
      minIndex,
    ],
  );
}

// ── Portal positioning ─────────────────────────────────────────────

/**
 * Keeps the dropdown positioned beneath the trigger element.
 * Handles scroll / resize listeners and cleans up on close.
 */
export function useDropdownPosition(
  open: boolean,
  triggerRef: RefObject<HTMLElement | null>,
  setDropdownPos: React.Dispatch<React.SetStateAction<DropdownPos | null>>,
  minWidth = 200,
  desiredHeight = 280,
) {
  useEffect(() => {
    if (!open || !triggerRef.current) {
      setDropdownPos(null);
      return;
    }
    const updatePos = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (rect) {
        const width = Math.max(rect.width, minWidth);
        setDropdownPos(
          computeDropdownPlacement(rect, desiredHeight, width, {
            width: window.innerWidth,
            height: window.innerHeight,
          }),
        );
      }
    };
    updatePos();
    window.addEventListener("scroll", updatePos, true);
    window.addEventListener("resize", updatePos);
    return () => {
      window.removeEventListener("scroll", updatePos, true);
      window.removeEventListener("resize", updatePos);
    };
  }, [open, triggerRef, setDropdownPos, minWidth, desiredHeight]);
}

// ── Focus on open / reset on close ─────────────────────────────────
/**
 * Focuses the search input when the dropdown opens and resets
 * search / highlighted index when it closes.
 *
 * Uses a bounded rAF retry loop so focus lands even when the input mounts
 * a render after `open` flips true (e.g. after useDropdownPosition computes
 * dropdownPos). Caps at MAX_FOCUS_RETRIES frames and cancels on cleanup so a
 * late frame cannot focus a closed or unmounted input.
 */
const MAX_FOCUS_RETRIES = 10;

export function useDropdownFocus(
  open: boolean,
  searchInputRef: RefObject<HTMLInputElement | null>,
  resetCallbacks: Array<() => void>,
) {
  useEffect(() => {
    if (!open) {
      for (const cb of resetCallbacks) cb();
      return;
    }

    let rafId: number;
    let retries = 0;

    function attempt() {
      const el = searchInputRef.current;
      if (el) {
        if (document.activeElement !== el) {
          el.focus();
        }
        return;
      }
      if (retries >= MAX_FOCUS_RETRIES) return;
      retries++;
      rafId = requestAnimationFrame(attempt);
    }

    rafId = requestAnimationFrame(attempt);

    return () => {
      cancelAnimationFrame(rafId);
    };
    // resetCallbacks is intentionally excluded — callers pass stable refs
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, searchInputRef]);
}

// ── Outside click ──────────────────────────────────────────────────
/**
 * Closes the dropdown when the user clicks outside of the trigger
 * and panel elements.
 */
export function useDropdownOutsideClick(
  open: boolean,
  refs: Array<RefObject<HTMLElement | null>>,
  onClose: () => void,
) {
  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      for (const ref of refs) {
        if (ref.current?.contains(e.target as Node)) return;
      }
      onClose();
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open, refs, onClose]);
}

// ── Composite portal dropdown ───────────────────────────────────────
export interface UsePortalDropdownOptions {
  /** Total number of selectable items */
  itemCount: number;
  /** Called when an item is selected by keyboard Enter */
  onSelect: (index: number) => void;
  /** Minimum dropdown width (defaults to 200) */
  width?: number;
  /**
   * Preferred max height of the dropdown panel. Used by viewport clamp /
   * flip logic. Defaults to 280px.
   */
  desiredHeight?: number;
  /** Stable callbacks to run when dropdown closes (e.g. reset searchQuery) */
  resetOnClose?: Array<() => void>;
  /**
   * Row highlighted when the dropdown opens (and restored on close). Defaults
   * to 0 — the first row. A list whose first row is an action rather than a
   * value (e.g. the "Custom" add-row) passes the index of its first real row,
   * so Enter straight after opening picks a value instead of firing the action.
   * Doubles as the keyboard floor (see `minIndex` on {@link useDropdownKeyboard}):
   * arrow keys never descend below it, and Enter refuses to fire under it.
   */
  initialHighlightedIndex?: number;
}

/**
 * Combines all portal dropdown state, refs, and hook calls into one.
 * Replaces the duplicated boilerplate in PartnerButton, LocationButton,
 * EmployeeSelector, and PartnerSelectWithTags.
 *
 * Pattern — compute items first, then call the hook:
 * ```ts
 * const [searchQuery, setSearchQuery] = useState("");
 * const items = useMemo(() => filterItems(data, searchQuery), [data, searchQuery]);
 * const dd = usePortalDropdown({ itemCount: items.length, onSelect: ..., width: 280 });
 * ```
 *
 * The hook manages open state, positioning, focus, outside-click, and keyboard.
 * It does NOT own searchQuery — the consumer owns it so items can be computed
 * before the hook is called (avoiding a circular dependency).
 */
export function usePortalDropdown({
  itemCount,
  onSelect,
  width = 200,
  desiredHeight = 280,
  resetOnClose = [],
  initialHighlightedIndex = 0,
}: UsePortalDropdownOptions) {
  const [open, setOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(
    initialHighlightedIndex,
  );
  const [dropdownPos, setDropdownPos] = useState<DropdownPos | null>(null);

  // Track whether the last highlight change came from keyboard navigation.
  // scrollIntoView should only fire for keyboard-driven changes — mouse hover
  // already has the item visible so scrolling is unnecessary and causes a
  // feedback loop (scroll → hover fires → highlight changes → scrollIntoView
  // fights the user's scroll).
  const isKeyboardNavRef = useRef(false);

  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const closeDropdown = useCallback(() => setOpen(false), []);
  const outsideClickRefs = useMemo(
    () => [triggerRef, dropdownRef] as Array<RefObject<HTMLElement | null>>,
    [],
  );

  useDropdownPosition(open, triggerRef, setDropdownPos, width, desiredHeight);
  useDropdownFocus(open, searchInputRef, [
    () => setHighlightedIndex(initialHighlightedIndex),
    ...resetOnClose,
  ]);
  useDropdownOutsideClick(open, outsideClickRefs, closeDropdown);

  const handleKeyDown = useDropdownKeyboard({
    itemCount,
    minIndex: initialHighlightedIndex,
    highlightedIndex,
    setHighlightedIndex: ((updater: React.SetStateAction<number>) => {
      isKeyboardNavRef.current = true;
      setHighlightedIndex(updater);
    }) as React.Dispatch<React.SetStateAction<number>>,
    onSelect,
    onClose: closeDropdown,
    triggerRef,
  });

  return {
    open,
    setOpen,
    highlightedIndex,
    setHighlightedIndex,
    dropdownPos,
    triggerRef,
    dropdownRef,
    searchInputRef,
    closeDropdown,
    handleKeyDown,
    isKeyboardNavRef,
  };
}
